<!--
  StoreBillingCard -- Distributor App > Account > Payments > "Store billing".

  Card: Per-store subscription billing (classify in arrears, invoice per
  distributor) -- https://trello.com/c/WJxkNmTo, pebble "PR6: distributor
  billing page (+ docs/user/)". Spec:
  docs/planning/store-counting-rules-2026-10-15.md 4.3 PR 6.

  Consumes GET /api/distributor/billing/stores (billingService.getStoreBilling)
  and loads its own data, so a failure here never takes down the rest of the
  Payments page. The tenant is resolved server-side; the month is the only
  parameter.

  States (from the payload's `state`):
    estimate     current month, month to date: table + "Estimated store charges so far"
    final        a closed month: table + "Total store charges"
    finalizing   a past month not closed yet: a message, no numbers
    unavailable  too many stores to show here: a message, no total (never $0)

  Reasons are server-built sentences; this component never maps codes.

  The `invoice` field is RESERVED for invoicing (PR5, not shipped). The slot
  below renders only when `showInvoiceStatus` is set AND the payload carries
  an invoice; nothing sets the prop today.
-->
<template>
  <v-card class="tf-store-billing-card" data-testid="store-billing-card">
    <v-card-title>Store billing</v-card-title>
    <v-card-subtitle>
      What each store costs for the month, and why.
    </v-card-subtitle>

    <v-card-text>
      <div class="tf-sb-toolbar">
        <v-select
          v-if="monthItems.length"
          :model-value="selectedMonth"
          :items="monthItems"
          item-title="title"
          item-value="value"
          label="Month"
          density="compact"
          variant="outlined"
          hide-details
          class="tf-sb-month"
          data-testid="store-billing-month-select"
          :disabled="loading"
          @update:modelValue="onMonthPicked"
        />
      </div>

      <!-- The plan's fee structure (flat fee, per-store fees, allowance):
           the same text as the plan card, from the server's one renderer.
           Listed even with no stores: a flat fee is owed regardless. -->
      <PlanFeeTerms
        v-if="!loading && !loadError && data"
        :fee-terms="data.fee_terms"
        testid="store-billing-fee-terms"
      />

      <v-progress-circular
        v-if="loading"
        :indeterminate="true"
        color="primary"
        size="24"
        width="3"
        class="mt-2"
        data-testid="store-billing-loading"
      />

      <p v-else-if="loadError" class="tf-sb-message" data-testid="store-billing-error">
        We couldn't load store billing. Please refresh to try again.
      </p>

      <p
        v-else-if="state === 'finalizing'"
        class="tf-sb-message"
        data-testid="store-billing-finalizing"
      >
        This month is being finalized. Its amounts appear here once it closes.
      </p>

      <p
        v-else-if="state === 'unavailable'"
        class="tf-sb-message"
        data-testid="store-billing-unavailable"
      >
        You have too many stores to show this month's breakdown here. Your
        bill is still worked out store by store when the month closes.
      </p>

      <p
        v-else-if="data && stores.length === 0"
        class="tf-sb-message"
        data-testid="store-billing-empty"
      >
        No stores to bill for this month.
      </p>

      <template v-else-if="data">
        <v-table density="compact" class="tf-sb-table" data-testid="store-billing-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Billed as</th>
              <th>Why</th>
              <th class="tf-sb-amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in stores"
              :key="row.store_id"
              :data-testid="`store-billing-row-${row.store_id}`"
            >
              <td :data-testid="`store-billing-name-${row.store_id}`">
                {{ row.store_name || "Store no longer available" }}
              </td>
              <td>
                <v-chip
                  size="small"
                  label
                  :color="classColor(row.class)"
                  :data-testid="`store-billing-class-${row.store_id}`"
                >{{ classLabel(row.class) }}</v-chip>
              </td>
              <td :data-testid="`store-billing-reason-${row.store_id}`">{{ row.reason }}</td>
              <td class="tf-sb-amount">
                <div :data-testid="`store-billing-amount-${row.store_id}`">{{ formatCents(row.amount_cents) }}</div>
                <div
                  v-if="row.price_label"
                  class="tf-sb-price-label"
                  :data-testid="`store-billing-price-label-${row.store_id}`"
                >{{ row.price_label }}</div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="tf-sb-total-row">
              <td colspan="3" data-testid="store-billing-total-label">{{ totalLabel }}</td>
              <td class="tf-sb-amount" data-testid="store-billing-total">{{ formatCents(data.total_cents) }}</td>
            </tr>
          </tfoot>
        </v-table>

        <p
          v-if="state === 'estimate'"
          class="tf-sb-footnote"
          data-testid="store-billing-estimate-note"
        >
          Amounts for the current month are estimates until the month closes.
        </p>
        <p v-if="data.note" class="tf-sb-footnote" data-testid="store-billing-note">
          {{ data.note }}
        </p>

        <!-- RESERVED: invoice status for this month (per-store billing PR5).
             Hidden by default; do not enable until invoicing ships. -->
        <div
          v-if="showInvoiceStatus && data.invoice"
          class="tf-sb-footnote"
          data-testid="store-billing-invoice"
        />
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { getStoreBilling } from "@/services/billingService";
import PlanFeeTerms from "./PlanFeeTerms.vue";

const CLASS_LABELS = { full: "FULL", dormant: "INACTIVE", none: "No charge" };
const CLASS_COLORS = { full: "primary", dormant: "warning", none: undefined };
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default {
  name: "StoreBillingCard",

  components: { PlanFeeTerms },

  props: {
    // Reserved for invoicing (PR5). Leave false until invoices exist.
    showInvoiceStatus: { type: Boolean, default: false },
  },

  data() {
    return {
      data: null,
      loading: true,
      loadError: false,
      selectedMonth: null,
      months: [],
    };
  },

  computed: {
    state() {
      return this.data ? this.data.state : null;
    },
    stores() {
      return Array.isArray(this.data?.stores) ? this.data.stores : [];
    },
    totalLabel() {
      return this.state === "estimate"
        ? "Estimated store charges so far"
        : "Total store charges";
    },
    monthItems() {
      return this.months.map((m) => ({
        value: m.month,
        title: this.monthTitle(m),
      }));
    },
  },

  mounted() {
    this.load(undefined);
  },

  methods: {
    async load(month) {
      this.loading = true;
      this.loadError = false;
      try {
        const res = await getStoreBilling(month);
        const body = res ? res.data : null;
        this.data = body;
        if (body && Array.isArray(body.months)) this.months = body.months;
        this.selectedMonth = body ? body.month : null;
      } catch (e) {
        this.data = null;
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    onMonthPicked(month) {
      if (!month || month === this.selectedMonth) return;
      this.selectedMonth = month;
      this.load(month);
    },

    monthTitle(m) {
      const [y, mm] = String(m.month).split("-");
      const name = MONTH_NAMES[Number(mm) - 1] || m.month;
      const base = `${name} ${y}`;
      if (m.state === "estimate") return `${base} (so far)`;
      if (m.state === "finalizing") return `${base} (being finalized)`;
      return base;
    },

    classLabel(cls) {
      return CLASS_LABELS[cls] || "Not classified";
    },

    classColor(cls) {
      return CLASS_COLORS[cls];
    },

    formatCents(cents) {
      if (cents == null) return "—";
      return `$${(cents / 100).toFixed(2)}`;
    },
  },
};
</script>

<style scoped>
.tf-store-billing-card {
  padding: var(--tf-sp-2, 8px);
}
.tf-sb-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}
.tf-sb-month {
  max-width: 260px;
}
.tf-sb-message {
  font-size: 13px;
  color: var(--tf-fg-3, #56636e);
}
.tf-sb-table {
  font-size: 13px;
}
.tf-sb-amount {
  text-align: right;
  white-space: nowrap;
}
.tf-sb-price-label {
  font-size: 11px;
  color: var(--tf-fg-4, #8a97a3);
}
.tf-sb-total-row td {
  font-weight: 500;
  color: var(--tf-fg-1, #1a2733);
  border-top: 1px solid var(--tf-border, #e3e8ec);
}
.tf-sb-footnote {
  font-size: 12px;
  color: var(--tf-fg-3, #56636e);
  margin-top: 8px;
}
</style>
